import React from 'react';
import { motion } from 'framer-motion';
import { Award, Target, Users, Shield, Trophy, Medal, Zap, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Coaches() {
  const coaches = [
    {
      name: "Coach Vikash Yadav",
      title: "Head Coach & Founder",
      role: "Olympic Level Coach",
      bio: "With 15+ years of experience in competitive shooting, Coach Vikash has trained dozens of state and national champions. NRAI certified technical coach with expertise in Air Rifle and Air Pistol disciplines.",
      achievements: [
        "NRAI Certified Technical Coach",
        "National Level Competitor",
        "Trained 20+ State Champions",
        "5+ National Medal Winners"
      ],
      specialties: ["Air Rifle", "Mental Coaching", "Competition Training"],
      image: "VY", // Initials
      featured: true,
    },
    {
      name: "Coach Priya Singh",
      title: "Assistant Coach",
      role: "Women's Training Specialist",
      bio: "Specialized in women's shooting programs and beginner training. Former national competitor with passion for developing young talent and improving shooting technique.",
      achievements: [
        "Former National Competitor",
        "Specialized in Youth Training",
        "Expert in Beginner Programs",
        "Mental Conditioning Certified"
      ],
      specialties: ["Air Rifle", "Beginner Training", "Youth Programs"],
      image: "PS",
      featured: false,
    },
    {
      name: "Coach Rajesh Kumar",
      title: "Range Master",
      role: "Safety & Equipment Officer",
      bio: "Ensures all safety protocols are maintained and equipment is in perfect condition. 20+ years experience in range management and competitive shooting logistics.",
      achievements: [
        "Range Safety Officer Certified",
        "Equipment Maintenance Expert",
        "NRAI Certified",
        "Zero Incident Record"
      ],
      specialties: ["Safety Management", "Equipment Maintenance", "Range Operations"],
      image: "RK",
      featured: false,
    },
  ];

  const stats = [
    { number: "15+", label: "Years Experience", icon: Trophy },
    { number: "100+", label: "Students Trained", icon: Users },
    { number: "25+", label: "Champions Produced", icon: Medal },
    { number: "100%", label: "Safety Record", icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-black pt-32 pb-20">
      {/* Header */}
      <section className="px-6 mb-20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600/10 border border-red-600/20 mb-6">
              <Users size={14} className="text-red-600" />
              <span className="text-[10px] font-black tracking-[0.3em] uppercase text-red-600">Meet Our Team</span>
            </div>

            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black italic uppercase leading-[0.85] text-white tracking-tighter mb-6">
              ELITE <span className="text-gold">COACHES</span>
            </h1>

            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Learn from national-level certified coaches dedicated to transforming you into a precision shooting expert.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="px-6 mb-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 md:p-8 bg-zinc-900/50 border border-white/10 rounded-[2rem] text-center hover:border-gold/30 transition-all"
              >
                <stat.icon className="text-red-600 mx-auto mb-3" size={28} />
                <p className="text-2xl md:text-3xl font-black text-white mb-2">{stat.number}</p>
                <p className="text-zinc-300 text-xs md:text-sm font-bold uppercase tracking-wider">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Coach - Featured */}
      <section className="px-6 mb-24">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-[3rem] bg-gradient-to-r from-red-600/10 to-gold/5 border border-white/10 p-0"
          >
            <div className="grid md:grid-cols-2 gap-12 items-center p-8 md:p-12 lg:p-16">
              {/* Image/Avatar */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="relative flex items-center justify-center"
              >
                <div className="relative w-64 h-64 md:w-80 md:h-80">
                  {/* Glow background */}
                  <div className="absolute inset-0 bg-red-600/20 rounded-[3rem] blur-2xl" />
                  {/* Avatar Frame with Image */}
                  <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-br from-red-600/30 to-gold/20 border-2 border-gold/40 overflow-hidden shadow-2xl">
                    <img 
                      src="/images/coach-vikash.png" 
                      alt="Head Coach Vikash Yadav"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full hidden items-center justify-center text-7xl font-black text-gold font-heading">
                      VY
                    </div>
                  </div>
                  {/* Decorative elements */}
                  <div className="absolute -top-4 -right-4 w-20 h-20 bg-gold/20 rounded-full" />
                  <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-red-600/10 rounded-full" />
                </div>
              </motion.div>

              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-red-600/20 border border-red-600/30 mb-4">
                  <Star className="text-red-600" size={14} />
                  <span className="text-[9px] font-black uppercase tracking-widest text-red-600">Head Coach</span>
                </div>

                <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white uppercase italic mb-2">
                  {coaches[0].name}
                </h2>
                <p className="text-gold font-black text-lg uppercase mb-6 tracking-wide">{coaches[0].title}</p>

                <p className="text-zinc-300 text-base leading-relaxed mb-8">
                  {coaches[0].bio}
                </p>

                <div className="mb-8">
                  <p className="text-white font-black uppercase text-sm mb-4 tracking-wide">Key Achievements</p>
                  <ul className="space-y-2">
                    {coaches[0].achievements.map((achievement, i) => (
                      <li key={i} className="flex items-center gap-3 text-zinc-300">
                        <div className="w-2 h-2 rounded-full bg-gold" />
                        {achievement}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-8">
                  <p className="text-white font-black uppercase text-sm mb-4 tracking-wide">Specialties</p>
                  <div className="flex flex-wrap gap-2">
                    {coaches[0].specialties.map((specialty, i) => (
                      <span 
                        key={i}
                        className="px-4 py-2 bg-red-600/20 border border-red-600/30 rounded-full text-red-600 text-xs font-black uppercase"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex gap-4">
                  <a 
                    href="tel:+919653524055"
                    className="px-6 py-3 bg-red-600 text-white font-black rounded-xl hover:bg-red-700 transition-all flex items-center gap-2"
                  >
                    <span>Book Session</span>
                    <Zap size={16} />
                  </a>
                  <Link 
                    to="/#contact"
                    className="px-6 py-3 border border-white/20 text-white font-black rounded-xl hover:bg-white/5 transition-all"
                  >
                    Contact
                  </Link>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Other Coaches */}
      <section className="px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-black text-white uppercase italic mb-12">Supporting Coaches</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {coaches.slice(1).map((coach, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i + 1) * 0.1 }}
                className="group p-8 bg-zinc-900/50 border border-white/10 rounded-[2rem] hover:border-gold/30 transition-all"
              >
                <div className="flex items-center gap-6 mb-6">
                  <div className="w-20 h-20 rounded-2xl bg-red-600/20 flex items-center justify-center text-2xl font-black text-red-600 group-hover:scale-110 transition-transform">
                    {coach.image}
                  </div>
                  <div>
                    <h4 className="text-white font-black text-xl uppercase">{coach.name}</h4>
                    <p className="text-gold font-black text-sm">{coach.title}</p>
                  </div>
                </div>

                <p className="text-zinc-300 text-sm leading-relaxed mb-6">
                  {coach.bio}
                </p>

                <div className="mb-6">
                  <p className="text-white font-black uppercase text-xs mb-3 tracking-wide">Specialties</p>
                  <div className="flex flex-wrap gap-2">
                    {coach.specialties.map((specialty, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-white text-[11px] font-bold"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>

                <button className="w-full py-2 px-4 bg-white/5 border border-white/10 text-white font-black rounded-lg hover:bg-red-600/20 hover:border-red-600/30 transition-all text-sm uppercase">
                  Learn More
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 mt-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-12 bg-gradient-to-r from-red-600/20 to-gold/10 border border-white/10 rounded-3xl text-center"
          >
            <h3 className="text-2xl md:text-3xl font-black text-white uppercase italic mb-4">
              Ready to Train with Our Elite Coaches?
            </h3>
            <p className="text-zinc-300 mb-8">
              Book a free trial session today and discover the difference professional coaching can make.
            </p>
            <Link 
              to="/book-demo"
              className="inline-flex items-center gap-2 px-8 py-4 bg-red-600 text-white font-black rounded-2xl hover:bg-red-700 transition-all text-lg uppercase"
            >
              <Target size={20} />
              Book Free Trial
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
